import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Send, Headphones } from "lucide-react";
import { motion } from "motion/react";
import { useAppStore } from "../store/useAppStore";
import { supportService, type SupportMessageDto } from "../services/supportService";
import { maxPlatform } from "../services/maxPlatform";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { toast } from "sonner";
import { linkifyText } from "../utils/linkifyText";
import { useTranslation } from "react-i18next";
import { formatApiError } from "../api/client";

type NavigateAction = { type: "navigate"; label: string; path: string };

function actionsFromMeta(meta?: Record<string, unknown>): NavigateAction[] {
  const raw = meta?.actions;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a): a is NavigateAction =>
      !!a &&
      typeof a === "object" &&
      (a as NavigateAction).type === "navigate" &&
      typeof (a as NavigateAction).label === "string" &&
      typeof (a as NavigateAction).path === "string",
  );
}

export function SupportChatPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedEventId, setNavbarVisible } = useAppStore();
  const [messages, setMessages] = useState<SupportMessageDto[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const title = t("visitor.support_title");
  const subtitle = t("visitor.support_subtitle");

  useEffect(() => {
    setNavbarVisible(false);
    return () => setNavbarVisible(true);
  }, [setNavbarVisible]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    requestAnimationFrame(() => {
      const list = listRef.current;
      if (list) {
        list.scrollTo({ top: list.scrollHeight, behavior });
        return;
      }
      bottomRef.current?.scrollIntoView({ behavior, block: "end" });
    });
  }, []);

  const load = useCallback(async () => {
    if (!selectedEventId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await supportService.getChat(selectedEventId);
      setMessages(data.messages);
      setTopics(data.topics ?? []);
      scrollToBottom("auto");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("visitor.loading_error"));
    } finally {
      setLoading(false);
    }
  }, [selectedEventId, scrollToBottom, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Pull admin/manager replies while the helper chat is open.
  useEffect(() => {
    if (!selectedEventId) return;
    const tick = window.setInterval(() => {
      if (document.visibilityState !== "visible" || sending) return;
      void supportService.getChat(selectedEventId).then((data) => {
        setMessages((prev) => {
          if (prev.length === data.messages.length) {
            const lastPrev = prev[prev.length - 1];
            const lastNext = data.messages[data.messages.length - 1];
            if (lastPrev?.id === lastNext?.id && lastPrev?.content === lastNext?.content) {
              return prev;
            }
          }
          return data.messages;
        });
        if (data.topics?.length) setTopics(data.topics);
      }).catch(() => undefined);
    }, 4000);
    return () => window.clearInterval(tick);
  }, [selectedEventId, sending]);

  useEffect(() => {
    if (selectedEventId) supportService.syncContext(selectedEventId);
  }, [selectedEventId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, sending, keyboardInset, scrollToBottom]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInset(inset > 40 ? inset : 0);
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("focusin", update);
    window.addEventListener("focusout", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("focusin", update);
      window.removeEventListener("focusout", update);
    };
  }, []);

  const send = async (text: string) => {
    const message = text.trim();
    if (!message || !selectedEventId || sending) return;
    setSending(true);
    setInput("");
    maxPlatform.hapticFeedback("light");
    const optimistic: SupportMessageDto = {
      id: Date.now(),
      role: "user",
      content: message,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    try {
      const data = await supportService.sendMessage(selectedEventId, message);
      setMessages(data.messages);
      setTopics(data.topics ?? topics);
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setInput(message);
      toast.error(formatApiError(err, t("visitor.send_failed")));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="flex flex-col flex-1 min-h-0 h-full overflow-hidden"
      style={{ paddingBottom: keyboardInset > 0 ? keyboardInset : undefined }}
    >
      <PageHeader title={title} subtitle={subtitle} back backTo="/my" />
      <PageShell noBottomPad className="flex flex-col flex-1 min-h-0 !pb-0 overflow-hidden">
        {!selectedEventId ? (
          <div className="mt-10 text-center text-[13px] text-gray-400 px-6">
            {t("visitor.select_event_first")}
          </div>
        ) : loading ? (
          <div className="flex justify-center py-16 text-gray-400">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : (
          <>
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto overscroll-contain space-y-3 pt-3 pb-2 min-h-0"
            >
              {messages.map((m) => {
                const actions = m.role !== "user" ? actionsFromMeta(m.meta) : [];
                const isManager =
                  m.meta?.kind === "manager_reply" || m.meta?.fromAdmin === true;
                return (
                  <motion.div
                    key={`${m.id}-${m.createdAt}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {m.role !== "user" && (
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 flex-shrink-0 mt-0.5 ${
                          isManager
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-sky-100 text-sky-600"
                        }`}
                      >
                        <Headphones size={14} />
                      </div>
                    )}
                    <div className="max-w-[82%] space-y-2">
                      {m.role !== "user" && isManager ? (
                        <p className="text-[10px] text-emerald-700 pl-0.5">Менеджер</p>
                      ) : null}
                      <div
                        className={`rounded-2xl px-3.5 py-2.5 text-[14px] leading-snug whitespace-pre-wrap break-words ${
                          m.role === "user"
                            ? "bg-indigo-500 text-white rounded-br-md"
                            : isManager
                              ? "bg-emerald-50 border border-emerald-100 text-emerald-950 rounded-bl-md shadow-sm"
                              : "bg-white border border-black/5 text-gray-800 rounded-bl-md shadow-sm"
                        }`}
                      >
                        {linkifyText(
                          m.content,
                          m.role === "user"
                            ? "underline underline-offset-2 text-white/95 break-all"
                            : "underline underline-offset-2 text-indigo-600 break-all",
                        )}
                      </div>
                      {actions.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {actions.map((a) => (
                            <button
                              key={`${a.path}-${a.label}`}
                              type="button"
                              onClick={() => {
                                maxPlatform.hapticFeedback("light");
                                navigate(a.path);
                              }}
                              className="h-8 px-3 rounded-full bg-indigo-50 text-indigo-700 text-[12px] border border-indigo-100 active:bg-indigo-100"
                            >
                              {a.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
              {sending && (
                <div className="flex items-center gap-2 text-[12px] text-gray-400 pl-10">
                  <Loader2 size={14} className="animate-spin" />
                  {t("visitor.typing")}
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {topics.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-none flex-shrink-0">
                {topics.map((topic) => {
                  const isManager =
                    topic.toLocaleLowerCase("ru-RU") === "связь с менеджером" ||
                    /manager|connect/i.test(topic);
                  return (
                    <button
                      key={topic}
                      type="button"
                      disabled={sending}
                      onClick={() => void send(topic)}
                      className={`flex-shrink-0 h-8 px-3 rounded-full text-[12px] border disabled:opacity-50 ${
                        isManager
                          ? "bg-amber-50 text-amber-800 border-amber-100 active:bg-amber-100"
                          : "bg-sky-50 text-sky-700 border-sky-100 active:bg-sky-100"
                      }`}
                    >
                      {topic}
                    </button>
                  );
                })}
              </div>
            )}

            <form
              className="flex items-end gap-2 pt-1 pb-[max(0.5rem,env(safe-area-inset-bottom))] flex-shrink-0 bg-transparent"
              onSubmit={(e) => {
                e.preventDefault();
                void send(input);
              }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onFocus={() => {
                  window.setTimeout(() => scrollToBottom("smooth"), 80);
                  window.setTimeout(() => scrollToBottom("smooth"), 320);
                }}
                placeholder={t("visitor.write_question")}
                disabled={sending}
                className="flex-1 h-11 px-4 rounded-xl bg-white border border-black/8 text-[14px] outline-none focus:border-indigo-300 disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="h-11 w-11 rounded-xl bg-indigo-500 text-white flex items-center justify-center disabled:opacity-40 active:bg-indigo-600"
              >
                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </form>
          </>
        )}
      </PageShell>
    </div>
  );
}
